/// <reference types="vite/client" />
import React, { useMemo, useState } from 'react';
import { ClerkProvider, useAuth } from '@clerk/clerk-react';
import { ConvexReactClient } from 'convex/react';
import { ConvexProviderWithClerk } from 'convex/react-clerk';
import { QueryClientProvider } from '@tanstack/react-query';
import {
  Outlet,
  createRootRouteWithContext,
  HeadContent,
  Scripts,
} from '@tanstack/react-router';
import { AppProvider } from '../contexts/AppContext';
import { AuthProvider } from '../contexts/AuthContext';
import { LanguageProvider } from '../contexts/LanguageContext';
import NotificationSystem from '../components/shared/NotificationSystem';
import ErrorBoundary from '../components/shared/ErrorBoundary';
import { queryClient } from '../queryClient';

const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
// Support both Vite (client) and process.env (SSR) so Convex always gets an absolute URL
const convexUrl =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_CONVEX_URL) ||
  (typeof process !== 'undefined' && process.env?.VITE_CONVEX_URL) ||
  '';
// ConvexReactClient requires an absolute URL (must include "://"); avoid passing empty string during SSR
const convexUrlOrPlaceholder =
  convexUrl && convexUrl.includes('://') ? convexUrl : 'https://placeholder.convex.cloud';

export const Route = createRootRouteWithContext<{ queryClient: typeof queryClient }>()({
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1, viewport-fit=cover' },
      { title: 'Pourvoirie Pignon Rouge' },
    ],
  }),
  component: RootComponent,
});

function RootComponent() {
  const [client] = useState(() => queryClient);
  const convexClient = useMemo(() => new ConvexReactClient(convexUrlOrPlaceholder), []);

  if (!publishableKey) {
    return (
      <div className="max-w-xl p-6 text-red-700 space-y-2">
        <p className="font-semibold">Missing Clerk publishable key</p>
        <p className="text-sm text-red-600">
          Set <code className="bg-red-50 px-1 rounded">VITE_CLERK_PUBLISHABLE_KEY</code> (starts with{' '}
          <code className="bg-red-50 px-1 rounded">pk_live_</code> or <code className="bg-red-50 px-1 rounded">pk_test_</code>).
        </p>
        <ul className="text-sm text-red-600 list-disc pl-5 space-y-1">
          <li>
            <strong>Local:</strong> add it to <code>.env.local</code>, then restart <code>pnpm dev</code>.
          </li>
          <li>
            <strong>Production (Vercel, etc.):</strong> Project → Settings → Environment Variables → add{' '}
            <code>VITE_CLERK_PUBLISHABLE_KEY</code> for <strong>Production</strong>, then redeploy.
          </li>
        </ul>
        <p className="text-xs text-red-500">
          <code>.env.local</code> is not deployed — hosting must define <code>VITE_*</code> vars at build time.
        </p>
      </div>
    );
  }

  return (
    <RootDocument>
      <ErrorBoundary>
        <ClerkProvider publishableKey={publishableKey}>
          <ConvexProviderWithClerk client={convexClient} useAuth={useAuth}>
            <QueryClientProvider client={client}>
              <AppProvider>
                <LanguageProvider>
                  <AuthProvider>
                    <Outlet />
                    <NotificationSystem />
                  </AuthProvider>
                </LanguageProvider>
              </AppProvider>
            </QueryClientProvider>
          </ConvexProviderWithClerk>
        </ClerkProvider>
      </ErrorBoundary>
    </RootDocument>
  );
}

function RootDocument({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <html lang="fr">
      <head>
        <HeadContent />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Ballet:opsz@16..72&family=Lavishly+Yours&display=swap"
        />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}
